<template lang="pug">
  .program-card.width-100.row.relative
    options.m-top-1.absolute(
      v-if='showOptions'
      v-on-clickaway='toggleOptions'
      :program='program'
      :approvedLanguages='approvedLanguages'
      :availableLanguages='availableLanguages'
      :disapprovedLanguages='disapprovedLanguages'
      :isTrial='isTrial'
      :isOwner='false'
      :draft='draft'
    )
    router-link.image-container.relative.item-7(:to='programLink')
      img.novelty-badge(:src='require(`../../assets/${language}_noveltyBadge.svg`)' width='165px' height='165px' v-if='program.novelty')
      img.image(:src='program.imageURL' v-if='program.imageURL' :alt='program.title')
    .info.item-8.column
      .row.middle.m4-bottom-1
        .row.middle.category(v-if='showCategory')
          img.category-img.m4-right-2(:src='programLogo' v-if='!!programLogo')
          span.text-normal.dark-gray {{ $t(`listPrograms.category.${programCategory.referenceId}`) }}
        .row.middle.flags.m-left-auto
          simple-svg(
            v-for='language in draft ? disapprovedLanguages : approvedLanguages'
            :filepath='getLanguageIcon(language)'
            width='21px'
            height='21px'
          )
        button.relative.m-left-05(v-if='withOptions' @click='toggleOptions')
          simple-svg(:filepath='optionsIcon' width='15px' height='15px')
      router-link(:to='programLink')
        h3.title-medium.blue {{ program.title || $t("programCard.untitled") }}
      .m-top-auto
        .used-content.row.middle.m-bottom-1(v-if='program.usedEducationalProgram' @click='showUsedContentDrawer')
          simple-svg.fern-green-icon.m-right-05(:filepath='checkIcon' width='15px' height='15px')
          span.text-small.fern-green.underline.pointer.m-left-05 {{ $t("programCard.seeUsedRecord") }}
        .row.m-bottom-2(v-if='isTeachersTraining' :style='teachersTrainingBadge')
          span.teachers-training-badge.relative.text-small {{ $t('resourceCard.category.teachersTraining') }}
        program-features(:program='program' navigation small)
        .row.middle.m4-top-1
          img.author-avatar(:src='programAuthor.picture || defaultProfileImage' :alt='`${programAuthor.name} avatar`')
          span.text-normal.grow {{ programAuthor.name }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ProgramFeatures from './ProgramFeatures'
import { mixin as clickaway } from 'vue-clickaway'
import rightArrow from '@/assets/ic_chevron-right.svg'
import defaultProfileImage from '@/assets/empty_profile.svg'
import defaultProgramImage from '@/assets/hardcodes/sample-program.png'
import { educationalProgramLanguageStatus } from '../../utils/constants'
import { allLanguages } from '../../constants/languages'
import optionsIcon from '@/assets/ic_dots_menu.svg'
import { routes } from '../../router'
import userRole from '../../mixins/userRole'
import checkIcon from '@/assets/ic_check.svg'
import { teachersTrainingReferenceId } from '../../constants/resource'
import Options from './ProgramCardOptions'

export default {
  mixins: [ clickaway, userRole ],
  data() {
    return {
      checkIcon,
      rightArrow,
      defaultProfileImage,
      defaultProgramImage,
      optionsIcon,
      routes,
      showOptions: false
    }
  },
  props: {
    program: { type: Object, required: true },
    withOptions: { type: Boolean },
    draft: { type: Boolean },
    goToEdition: { type: Boolean }
  },
  computed: {
    ...mapState({
      categories: state => state.dashboard.categories,
      language: state => state.behavior.locale
    }),
    showCategory() {
      return this.program.categoryId !== Number(process.env.VUE_APP_DEFAULT_CONTENT_CATEGORY)
    },
    teachersTrainingBadge() {
      return {
        '--category-color': this.program.categoryColor
      }
    },
    isTeachersTraining() {
      return this.program.categoryReferenceId === teachersTrainingReferenceId
    },
    programLogo() {
      return this.programCategory ? this.programCategory.imageURL : ''
    },
    programCategory() {
      return this.categories.find(category => category.id === this.program.categoryId)
    },
    programAuthor() {
      return this.program.author || this.program.mainAuthor
    },
    isKrea() {
      return this.programCategory.id === this.categories.find(cat => cat.name === 'Krea').id
    },
    availableLanguages() {
      return Object.keys(this.program.activatedLanguages)
    },
    approvedLanguages() {
      return this.availableLanguages.filter(key => this.program.activatedLanguages[key] !== educationalProgramLanguageStatus.hidden)
    },
    disapprovedLanguages() {
      return this.availableLanguages.filter(key => this.program.activatedLanguages[key] === educationalProgramLanguageStatus.hidden)
    },
    programLink() {
      if (this.goToEdition) {
        return {
          name: routes.kreaEducationalContent,
          params: { id: this.program.id },
          query: {
            locale: this.draft
              ? (this.availableLanguages.includes(this.language) && this.language) || this.availableLanguages[0]
              : (this.approvedLanguages.includes(this.language) && this.language) || this.approvedLanguages[0]
          }
        }
      } else {
        return {
          name: routes.educationalProgram,
          params: { id: this.program.id }
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'setKreaEducationalContentStatus'
    ]),
    showUsedContentDrawer() {
      this.showElement({
        element: 'usedContentDrawer',
        props: { contentId: this.program.id }
      })
    },
    getLanguageIcon(language) {
      return allLanguages.find(lang => lang.lang === language).icon
    },
    toggleOptions() {
      this.showOptions = !this.showOptions
    },
    showPublishContentModal() {
      this.showElement({
        element: 'kreaPublishContentModal',
        props: {
          contentId: this.program.id,
          keywords: this.program.keywords,
          languages: this.disapprovedLanguages,
          completeness: this.program.completeness
        }
      })
    },
    showEditKeywordsModal() {
      this.showElement({
        element: 'kreaEditKeywordsContentModal',
        props: {
          contentId: this.program.id,
          keywords: this.program.keywords,
          availableLanguages: this.program.availableLanguages
        }
      })
    },
    showDeleteContentModal() {
      this.showElement({
        element: 'kreaDeleteModal',
        props: {
          id: this.program.id,
          languages: this.availableLanguages,
          type: 'content'
        }
      })
    }
  },
  components: { ProgramFeatures, Options }
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables/colors';

.hidden{
  display: none;
}

.teachers-training-badge {
  display: inline-flex;
  width: auto;
  padding-left: 16px;

  &::before {
    background-color: var(--category-color);
    border-radius: 100%;
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 6px;
    width: 8px;
  }
}

.program-card {
  border: solid 1px $light-gray;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 $shadow-gray;
  height: 290px;
  position: relative;
  width: 100%;
}

.image-container{
  border-radius: 8px 0 0 8px;
  background: linear-gradient(180deg, $black 0%, $dark-gray-translusent 100%);
  overflow: hidden;
}

.novelty-badge {
  position: absolute;
  right: -2px;
  top: -8px;
  transform: rotate(-1deg);
  filter: drop-shadow(2px 2px 5px $shadow-blacker);
}

.info {
  padding: 15px;
}

.image {
  height: 100%;
  width: 100%;
  border-radius: 8px 0 0 8px;
  object-fit: cover;
}

.flags{
  &>:not(:last-child){
    margin-right: 5px;
  }
}

.category-img {
  width: 28px;
}

.author-avatar {
  box-sizing: border-box;
  height: 32px;
  width: 32px;
  border: 1px solid $light-gray;
  border-radius: 100%;
  margin-right: 8px;
}

.explore-icon {
  line-height: 10px;
}
</style>
